import { useRef, useCallback } from 'react'

export function useAudio() {
  const ctxRef = useRef<AudioContext | null>(null)
  const buffers = useRef<Map<string, AudioBuffer>>(new Map())

  const init = useCallback(() => {
    if (!ctxRef.current) {
      ctxRef.current = new AudioContext()
    }
    return ctxRef.current
  }, [])

  const load = useCallback(async (url: string) => {
    const ctx = init()
    if (buffers.current.has(url)) return buffers.current.get(url)!
    const res = await fetch(url)
    const arrayBuffer = await res.arrayBuffer()
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer)
    buffers.current.set(url, audioBuffer)
    return audioBuffer
  }, [init])

  const play = useCallback(async (url: string) => {
    const ctx = init()
    const buffer = await load(url)
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.connect(ctx.destination)
    source.start()
    return source
  }, [init, load])

  const playBeep = useCallback(() => {
    const ctx = init()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = 880
    gain.gain.setValueAtTime(0.3, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2)
    osc.start()
    osc.stop(ctx.currentTime + 0.2)
  }, [init])

  return { init, load, play, playBeep }
}
